import React from 'react';
import { Box, Flex, IconButton, InputDescription, TextInput } from '@fullstory/components';
import { NamedElementSelectorState } from '@fullstory/named-elements';
import { defaultOrControlLimit, OrControl } from '@fullstory/search-shared-ui';
import css from './index.module.scss';
import { SelectorRule } from '@fullstory/selectors';
import { useIntl } from 'react-intl';

interface NamedElementsSelectorInputProps {
  selectors: string[];
  selectorStates?: NamedElementSelectorState[];
  onChange: (selectors: string[]) => void;
  disabled?: boolean;
}

export const NamedElementSelectorInputs: React.FC<NamedElementsSelectorInputProps> = ({
  selectors,
  selectorStates,
  onChange,
  disabled,
}) => {
  const intl = useIntl();
  return (
    <>
      {selectors.map((sel, i) => (
        <React.Fragment key={i}>
          <Flex justifyContent="space-between">
            <Box className={css.selectorInput}>
              <TextInput
                onChange={e => {
                  const newSelectors = selectors.slice();
                  newSelectors[i] = e.target.value;
                  onChange(newSelectors);
                }}
                onBlur={_e => {
                  const newSelectors = selectors.slice();
                  newSelectors[i] = SelectorRule.format(sel) ?? sel;
                  onChange(newSelectors);
                }}
                value={sel}
                placeholder={intl.formatMessage({
                  defaultMessage: `e.g. button[data-component='checkout-button']`,
                  id: 'bpoQsa',
                })}
                disabled={disabled}
                invalid={selectorStates?.[i] === NamedElementSelectorState.Invalid}
                testId={`element-selector-text-input-${i}-text-input`}
                aria-labelledby="selector"
              />
            </Box>
            {selectors.length > 1 && !disabled && (
              <Box marginLeft={4}>
                <IconButton
                  iconName="close"
                  title={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahO' })}
                  variant="minimal"
                  onClick={() => {
                    const newSelectors = selectors.slice();
                    newSelectors.splice(i, 1);
                    onChange(newSelectors);
                  }}
                />
              </Box>
            )}
          </Flex>
          {selectorStates?.[i] === NamedElementSelectorState.Invalid && (
            <InputDescription
              invalid
              description={intl.formatMessage({
                defaultMessage: 'That selector has invalid syntax.',
                id: 'b6GUuB',
              })}
            />
          )}
          {selectorStates?.[i] === NamedElementSelectorState.SelectorInUse && (
            <InputDescription
              invalid
              description={intl.formatMessage({
                defaultMessage: "This selector's already being used.",
                id: 'V4rQnr',
              })}
            />
          )}
          {(i < selectors.length - 1 ||
            (selectors.length < defaultOrControlLimit && !disabled)) && (
            <OrControl
              onClick={
                i === selectors.length - 1 && !disabled
                  ? () => {
                      const newSelectors = selectors.slice();
                      newSelectors.push('');
                      onChange(newSelectors);
                    }
                  : undefined
              }
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
};
